import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import AppConfig from '@/AppConfig';
import {
  JWTPayload,
  decodeAuthToken,
  getToken,
  getTokenWithRetry,
  redirectToPlumApp,
  track,
  verifyTokenAndInit,
} from '@/utils';
import safeJsonParse from '@/utils/safeJsonParse';
import {
  EMessageType,
  IHomepageQueryResponse,
  IMessagePayload,
  IUserConfig,
} from '@/types';
import { useQuery } from '@tanstack/react-query';
import { authenticateMeQuery } from '@/queries/auth.queries';
import { FEATURE_FLAGS } from '@/constants';
import { homePageQuery } from '@/queries/helpline.queries';
import { isUnauthenticatedRhiPaymentReturn } from '@/utils/rhiPaymentReturnAuth';
import { isUnauthenticatedStuPaymentReturn } from '@/utils/stuPaymentReturnAuth';
import { isAgentLaunch } from '@/utils/agentSession';

const IS_WEBVIEW_STORAGE_KEY = 'isWebView';
const IS_NATIVE_CHAT_STORAGE_KEY = 'isNativeChat';
const NATIVE_CHAT_PARAM = 'nativeChat';

const AppContext = createContext<{
  isWebView: boolean;
  isNativeChat: boolean;
  xPlumToken: string | null;
  decodedToken: JWTPayload | null;
  isAppLoading: boolean;
  userConfig: IUserConfig | null;
  startRecording: () => void;
  stopRecording: () => void;
  homepageData?: IHomepageQueryResponse;
}>({
  isWebView: false,
  isNativeChat: false,
  xPlumToken: null,
  decodedToken: null,
  isAppLoading: false,
  userConfig: null,
  startRecording: () => {},
  stopRecording: () => {},
});

const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const isWebViewParam = searchParams.get('webview') === 'true';
  // `?nativeChat=true` is sent by a native shell that renders its own support
  // chat over this WebView; absent/any other value means it does not.
  const nativeChatParam = searchParams.get(NATIVE_CHAT_PARAM);

  // Synchronously computed once per mount. Prevents global auth redirects and
  // authenticated queries from firing when a WebView payment gateway return
  // lands on this app without an auth token.
  // Either journey's payment gateway return, reached without a session.
  const shouldSkipAuthForRhiPaymentReturn =
    isUnauthenticatedRhiPaymentReturn() || isUnauthenticatedStuPaymentReturn();
  const skipAuthRedirect =
    shouldSkipAuthForRhiPaymentReturn || isAgentLaunch();

  const startRecording = () => {
    if (typeof window.smartlook === 'function') {
      window.smartlook('recording', 'true', {}); // Start recording
    }
  };

  const stopRecording = () => {
    if (typeof window.smartlook === 'function') {
      window.smartlook('recording', 'false', {}); // Stop recording
    }
  };

  useEffect(() => {
    if (isWebViewParam) {
      Cookies.set(IS_WEBVIEW_STORAGE_KEY, 'true');
      sessionStorage.setItem(IS_WEBVIEW_STORAGE_KEY, 'true');
    }
  }, [isWebViewParam]);

  const isWebView = useMemo(() => {
    return (
      isWebViewParam ||
      Cookies.get(IS_WEBVIEW_STORAGE_KEY) === 'true' ||
      sessionStorage.getItem(IS_WEBVIEW_STORAGE_KEY) === 'true'
    );
  }, [isWebViewParam]);

  // Persisted per WebView session (not in a cookie, so it never leaks into a
  // later mWeb visit) because in-app navigation drops query params — e.g. the
  // cashless status page's back-press handler clears them all.
  const isNativeChat = useMemo(() => {
    try {
      if (nativeChatParam === 'true') {
        sessionStorage.setItem(IS_NATIVE_CHAT_STORAGE_KEY, 'true');
        return true;
      }
      if (nativeChatParam != null) {
        sessionStorage.removeItem(IS_NATIVE_CHAT_STORAGE_KEY);
        return false;
      }
      return sessionStorage.getItem(IS_NATIVE_CHAT_STORAGE_KEY) === 'true';
    } catch {
      return nativeChatParam === 'true';
    }
  }, [nativeChatParam]);

  const [xPlumToken, setXPlumToken] = useState<string | null>(() => getToken());

  useEffect(() => {
    const refreshTokenOnVisibility = async () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      const freshToken = getToken() ?? (await getTokenWithRetry());
      if (freshToken) {
        setXPlumToken(freshToken);
      } else {
        setXPlumToken(null);
      }
    };

    document.addEventListener('visibilitychange', refreshTokenOnVisibility);

    return () => {
      document.removeEventListener(
        'visibilitychange',
        refreshTokenOnVisibility,
      );
    };
  }, []);

  const { isLoading, isFetched, data } = useQuery({
    queryKey: authenticateMeQuery().queryKey,
    queryFn: authenticateMeQuery().queryFn,
    refetchInterval: 1000 * 60 * 15, // 15 minutes,
    refetchOnWindowFocus: !isWebView,
    refetchOnReconnect: !isWebView,
    refetchOnMount: true,
    enabled: !skipAuthRedirect,
  });

  useEffect(() => {
    window.dev.i('AppProvider', 'isFetched', {
      isFetched,
      hasToken: !!xPlumToken,
    });
    if (isFetched && !xPlumToken && !skipAuthRedirect) {
      const redirectToUrl = encodeURI(window.location.href);
      redirectToPlumApp(`/logout?redirectTo=${redirectToUrl}`)();
    }
  }, [isFetched, xPlumToken, skipAuthRedirect]);

  useEffect(() => {
    const decodedToken = isAgentLaunch()
      ? null
      : decodeAuthToken(xPlumToken ?? '');
    if (decodedToken) {
      const handleAnalyticsLoaded = () => {
        track.identifyUserToAnalytics(decodedToken);
      };

      window.addEventListener(
        AppConfig.ANALYTICS_LOADED_EVENT,
        handleAnalyticsLoaded,
      );
      // initTracker runs before React mount; identify immediately if CleverTap is already ready
      handleAnalyticsLoaded();

      return () => {
        window.removeEventListener(
          AppConfig.ANALYTICS_LOADED_EVENT,
          handleAnalyticsLoaded,
        );
      };
    }
  }, [xPlumToken]);

  /**
   * This useEffect listens for messages from the parent window
   * and verifies the token and initializes the app
   */
  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      const { data: message, origin } = event;

      if (import.meta.env.PROD && origin === AppConfig.PLUM_APP_HOST_URL) {
        const payload = safeJsonParse(message) as IMessagePayload;

        if (payload == null) {
          return;
        }

        if (payload?.type === EMessageType.TOKEN) {
          verifyTokenAndInit(String(payload?.data));
        }
      }
    };

    window.addEventListener('message', messageHandler);

    // Send ready message to parent window
    if (window.parent !== window) {
      window.parent.postMessage(
        JSON.stringify({
          type: EMessageType.READY,
          data: [EMessageType.TOKEN],
        }),
        AppConfig.PLUM_APP_HOST_URL,
      );
    }

    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, []);

  const isAppLoading = isLoading || !isFetched;

  useEffect(() => {
    if (data?.featureFlags) {
      const { featureFlags } = data;
      const isNewCashlessEnabled =
        featureFlags?.find?.((feat) => {
          return feat.name === FEATURE_FLAGS.NEW_CASHLESS_CREATE;
        })?.enabled ?? false;

      const isNetworkHospitalsEnabled =
        featureFlags?.find?.((feat) => {
          return feat.name === FEATURE_FLAGS.NETWORK_HOSPITALS_V3;
        })?.enabled ?? false;

      const isNHRoute = window.location.pathname === '/network-hospitals';
      const isCreateRoute = window.location.pathname === '/claims/create';

      if (
        (isNHRoute && !isNetworkHospitalsEnabled) ||
        (isCreateRoute && !isNewCashlessEnabled)
      ) {
        redirectToPlumApp('/claims')();
      }
    }
  }, [data]);

  useEffect(() => {
    if (data?.id && window?.smartlook) {
      window?.smartlook?.('init', AppConfig.SMARTLOOK_KEY, {
        region: 'eu',
      });
      window?.smartlook('identify', data?.id as string, {
        memberId: data?.id,
        organisationId: data?.organisation?.id,
        email: data?.email,
      });
    }
  }, [data]);

  const { data: homepageData } = useQuery({
    ...homePageQuery({ xPlumToken: xPlumToken ?? '' }),
    enabled: !skipAuthRedirect,
  });

  const values = useMemo(() => {
    const decodedToken = isAgentLaunch()
      ? null
      : decodeAuthToken(xPlumToken ?? '');

    return {
      isWebView,
      isNativeChat,
      xPlumToken,
      decodedToken,
      isAppLoading,
      userConfig: data as IUserConfig,
      startRecording,
      stopRecording,
      homepageData,
    };
  }, [isWebView, isNativeChat, xPlumToken, data, isAppLoading, homepageData]);

  return <AppContext.Provider value={values}>{children}</AppContext.Provider>;
};

const useAppContext = () => {
  const values = useContext(AppContext);
  if (!values) {
    throw new Error('App must be wrapped in AppProvider');
  }
  return values;
};

export { AppContext, useAppContext };
export default AppProvider;
